import React, { Fragment, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { MoreVertical, Edit, Trash2, Users, UserPlus, PlusCircle } from "react-feather";
import {
  Col,
  Row,
  Button,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Container,
} from "reactstrap";
import { baseURL } from "../../../../../Services/api/baseURL";
import Loader from "../../../../Loader/Loader";
import axios from "axios";
import { toast } from "react-toastify";
import ConfirmModal from "../../../../ConfirmModal/ConfirmModal";
import AddCustomersToTagModal from "./AddCustomersToTagModal";
import ViewTagCustomersModal from "./ViewTagCustomersModal";
import useRbac from "../../../../../Hooks/useRbac";
import CommonModal from "../../../../UiKits/Modals/common/modal";

const TagManagement = () => {
  const { can, canAny } = useRbac();
  const [isLoading, setIsLoading] = useState(false);
  const [tagsData, setTagsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentTag, setCurrentTag] = useState(null);

  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  // Customer modals
  const [addCustomersModalOpen, setAddCustomersModalOpen] = useState(false);
  const [viewCustomersModalOpen, setViewCustomersModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    description: "",
    category: "preference",
    color: "#6B7280",
  });

  const categories = [
    { value: "preference", label: "Preference" },
    { value: "behavior", label: "Behavior" },
    { value: "demographic", label: "Demographic" },
    { value: "engagement", label: "Engagement" },
    { value: "other", label: "Other" },
  ];

  const tagColumns = [
    {
      name: "Tag Name",
      selector: (row) => row.display_name,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <span
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: row.color,
              marginRight: "8px",
            }}
          ></span>
          <span style={{ fontWeight: "500" }}>{row.display_name}</span>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Category",
      selector: (row) => row.category,
      cell: (row) => (
        <span
          style={{
            padding: "4px 12px",
            borderRadius: "12px",
            backgroundColor: "#e3f2fd",
            color: "#1976d2",
            fontSize: "0.85rem",
            textTransform: "capitalize",
          }}
        >
          {row.category}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Description",
      selector: (row) => row.description || "N/A",
      width: "400px",
    },
    {
      name: "Customers",
      selector: (row) => row.customer_count || 0,
      cell: (row) => (
        <div
          onClick={() => handleViewCustomers(row)}
          style={{
            fontWeight: "600",
            color: "#1976d2",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {row.customer_count || 0}
        </div>
      ),
      sortable: true,
      center: true,
    },
    {
      name: "Actions",
      width: "100px",
      omit: !canAny(['tags.update', 'tags.delete']),
      cell: (row) => (
        <UncontrolledDropdown className="action_dropdown">
          <DropdownToggle className="action_btn">
            <MoreVertical color="#000" size={16} />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem onClick={() => handleAddCustomers(row)}>
              <UserPlus size={14} className="me-2" />
              Add Customers
            </DropdownItem>
            <DropdownItem onClick={() => handleViewCustomers(row)}>
              <Users size={14} className="me-2" />
              View Customers
            </DropdownItem>
            <DropdownItem divider />
            {can('tags.update') && <DropdownItem onClick={() => handleEdit(row)}>
              <Edit size={14} className="me-2" />
              Edit
            </DropdownItem>}
            {can('tags.delete') && <DropdownItem
              className="text-danger"
              onClick={() => handleDeleteClick(row)}
            >
              <Trash2 size={14} className="me-2" />
              Delete
            </DropdownItem>}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
      center: true,
    },
  ];

  useEffect(() => {
    fetchTags();
  }, [searchTerm, categoryFilter]);

  const fetchTags = async () => {
    setIsLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const params = {};

      if (searchTerm) params.search = searchTerm;
      if (categoryFilter) params.category = categoryFilter;

      const response = await axios.get(`${baseURL}/api/tags`, {
        params,
        headers: { Authorization: token },
      });

      if (response.status === 200) {
        setTagsData(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch tags");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = () => {
    setEditMode(false);
    setCurrentTag(null);
    setFormData({
      name: "",
      display_name: "",
      description: "",
      category: "preference",
      color: "#6B7280",
    });
    setModalOpen(true);
  };

  const handleEdit = (tag) => {
    setEditMode(true);
    setCurrentTag(tag);
    setFormData({
      name: tag.name,
      display_name: tag.display_name,
      description: tag.description || "",
      category: tag.category,
      color: tag.color,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Auto-generate slug from display name
    if (name === "display_name" && !editMode) {
      setFormData((prev) => ({
        ...prev,
        name: value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.display_name.trim()) {
      toast.error("Tag name is required");
      return;
    }

    try {
      const token = JSON.parse(localStorage.getItem("token"));

      if (editMode) {
        const response = await axios.put(
          `${baseURL}/api/tags/${currentTag._id}`,
          formData,
          { headers: { Authorization: token } }
        );

        if (response.status === 200) {
          toast.success("Tag updated successfully");
          fetchTags();
        }
      } else {
        const response = await axios.post(`${baseURL}/api/tags`, formData, {
          headers: { Authorization: token },
        });

        if (response.status === 201) {
          toast.success("Tag created successfully");
          fetchTags();
        }
      }

      setModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Operation failed");
      console.error(error);
    }
  };

  const handleDeleteClick = (tag) => {
    setTagToDelete(tag);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      await axios.delete(`${baseURL}/api/tags/${tagToDelete._id}`, {
        headers: { Authorization: token },
      });

      toast.success("Tag deleted successfully");
      setTagsData((prev) => prev.filter((tag) => tag._id !== tagToDelete._id));
      setDeleteModalOpen(false);
      setTagToDelete(null);
    } catch (error) {
      toast.error("Failed to delete tag");
      console.error(error);
    }
  };

  // ADDED: Missing function to handle adding customers
  const handleAddCustomers = (tag) => {
    setSelectedTag(tag);
    setAddCustomersModalOpen(true);
  };

  // ADDED: Missing function to handle viewing customers
  const handleViewCustomers = (tag) => {
    setSelectedTag(tag);
    setViewCustomersModalOpen(true);
  };

  // ADDED: Callback to refresh tags after customer operations
  const handleCustomersUpdated = () => {
    fetchTags();
  };

  return (
    <Fragment>
      <Row xxl={12} className="pb-2">
        <Col md={12} className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="font-bold">Tag Management</h5>
          <div className="d-flex align-items-center gap-2">
            {/* Category Filter */}
            <select
              className="form-select"
              style={{ width: "200px" }}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>

            {/* Search */}
            <div className="form-group position-relative search_outer d-flex align-items-center mb-0">
              <i className="fa fa-search"></i>
              <input
                className="form-control border-0 ms-2"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                type="text"
                placeholder="Search tags..."
              />
            </div>

            {/* Create Tag Button */}
            {can('tags.create') && <Button
              onClick={handleOpenModal}
              className="btn btn-primary d-flex align-items-center  ms-3"
            >
              <PlusCircle size={16} className="me-1" />
              Create Tag
            </Button>}
          </div>
        </Col>
      </Row>

      {isLoading ? (
        <Loader />
      ) : (
        <DataTable
          data={tagsData}
          columns={tagColumns}
          pagination
          paginationPerPage={10}
          highlightOnHover
          pointerOnHover
        />
      )}


      <CommonModal
        isOpen={modalOpen}
        title={editMode ? "Edit Tag" : "Create New Tag"}
        toggler={closeModal}
        size="lg"
        className="store_modal"
      >
        <Container>
          <Form onSubmit={handleSubmit}>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="display_name">
                      Tag Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="display_name"
                      id="display_name"
                      value={formData.display_name}
                      onChange={handleInputChange}
                      placeholder="e.g., Wine Lover"
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="name">Slug</Label>
                    <Input
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., wine-lover"
                      disabled={editMode}
                    />
                    <small className="text-muted">
                      Auto-generated from tag name
                    </small>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="category">Category</Label>
                    <Input
                      type="select"
                      name="category"
                      id="category"
                      value={formData.category}
                      onChange={handleInputChange}
                    >
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="color">Color</Label>
                    <div className="d-flex align-items-center gap-2">
                      <Input
                        type="color"
                        name="color"
                        id="color"
                        value={formData.color}
                        onChange={handleInputChange}
                        style={{ width: "60px", height: "38px" }}
                      />
                      <Input
                        type="text"
                        value={formData.color}
                        onChange={handleInputChange}
                        name="color"
                        placeholder="#6B7280"
                      />
                    </div>
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label for="description">Description</Label>
                <Input
                  type="textarea"
                  name="description"
                  id="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of this tag..."
                  rows={3}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button className="cancel_Btn" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn btn-primary">
                {editMode ? "Update Tag" : "Create Tag"}
              </Button>
            </ModalFooter>
          </Form>
        </Container >
      </CommonModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={`Delete "${tagToDelete?.display_name}"?`}
        description="Are you sure you want to delete this tag? It will be removed from all customers."
      />

      {/* Add Customers Modal */}
      <AddCustomersToTagModal
        isOpen={addCustomersModalOpen}
        toggle={() => setAddCustomersModalOpen(false)}
        tag={selectedTag}
        onSuccess={handleCustomersUpdated}
      />

      {/* View Customers Modal */}
      <ViewTagCustomersModal
        isOpen={viewCustomersModalOpen}
        toggle={() => setViewCustomersModalOpen(false)}
        tag={selectedTag}
      />
    </Fragment>
  );
};

export default TagManagement;
